/**
 * The replay artifact: a frozen past day for one concelho, our 1 km grid beside the
 * official IPMA/ICNF class for that same day.
 *
 * Baked by scripts/bake_replay.py from the PUBLIC LEDGER (not our database), so every
 * source file carries a SHA-256 and a commit-pinned permalink. The `provenance` block is
 * meant to be shown, including `o_que_isto_NAO_prova` — the honest limit is the point,
 * not a footnote.
 */

export interface ReplaySource {
  o_que: string;
  caminho: string;
  permalink: string;
  raw_url: string;
  sha256: string;
  bytes: number;
  commit: string;
  publicado_em: string;
}

export interface ReplayFreguesia {
  nome: string;
  dtmnfr: string | null;
  n_cells: number;
  risk: Record<string, number>;
  mod_plus: number;
  mod_plus_pct: number;
  mean_prob: number;
  max_prob: number;
}

export interface ReplayDoc {
  date: string;
  slug: string;
  nome: string;
  dico: string;
  generated_at: string;
  model_version: string;
  cycle: string;
  oficial: {
    fonte: string;
    ambito: string;
    d0: number | null;
    d0_label: string;
    d1: number | null;
    d1_label: string;
  };
  ignis: {
    n_cells: number;
    resolucao_m: number;
    risk: Record<string, number>;
    mod_plus: number;
    mod_plus_pct: number;
    min_prob: number;
    median_prob: number;
    p90_prob: number;
    max_prob: number;
    spread_max_over_min: number | null;
    spread_max_over_median: number | null;
  };
  freguesias: ReplayFreguesia[];
  freguesias_nomes: string[];
  cells: (number | null)[][]; // [lat, lon, prob, level, freguesia-index]
  leitura: string;
  provenance: {
    ledger: string;
    publico: boolean;
    branch_protegido: boolean;
    sources: ReplaySource[];
    como_verificar: string;
    o_que_isto_prova: string;
    o_que_isto_NAO_prova: string;
    validacao: string;
  };
}

/** null when there is no frozen replay for this (date, concelho). */
export async function loadReplay(date: string, slug: string): Promise<ReplayDoc | null> {
  const cdn = import.meta.env.VITE_DATA_URL;
  if (!cdn) return null;
  try {
    const res = await fetch(`${cdn}/replay/${date}/${slug}.json`);
    if (!res.ok) return null;
    return (await res.json()) as ReplayDoc;
  } catch {
    return null;
  }
}
